import React, { forwardRef, useMemo } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { ptBR } from "date-fns/locale/pt-BR";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("pt-BR", ptBR);

const isoMonthToDate = (value) => {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) return null;
  return new Date(`${value}-01T00:00:00`);
};

const dateToIsoMonth = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const formatMonthDisplay = (value) => {
  const date = isoMonthToDate(value);
  if (!date) return "";

  const formatted = new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
  }).format(date);

  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const MonthInput = forwardRef(
  (
    { value, onClick, disabled, placeholder, onBlur, onFocus, className, ...rest },
    ref
  ) => (
    <div
      ref={ref}
      {...rest}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      className={`fz-date-input${disabled ? " is-disabled" : ""}${className ? ` ${className}` : ""}`}
      onClick={disabled ? undefined : onClick}
      onBlur={onBlur}
      onFocus={onFocus}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.(event);
        }
      }}
    >
      <CalendarDays size={17} />
      <span
        className={`fz-month-input__value${value ? "" : " is-placeholder"}`}
      >
        {value || placeholder}
      </span>
    </div>
  )
);

MonthInput.displayName = "PainelMonthPickerInput";

const MonthPickerHeader = ({
  date,
  decreaseYear,
  increaseYear,
  prevYearButtonDisabled,
  nextYearButtonDisabled,
}) => (
  <div className="fz-date-header-panel fz-month-header-panel">
    <div className="fz-date-header">
      <button
        type="button"
        className="fz-date-nav"
        onClick={decreaseYear}
        disabled={prevYearButtonDisabled}
        aria-label="Ano anterior"
      >
        <ChevronLeft size={18} />
      </button>

      <strong className="fz-month-header-year">{date.getFullYear()}</strong>

      <button
        type="button"
        className="fz-date-nav"
        onClick={increaseYear}
        disabled={nextYearButtonDisabled}
        aria-label="Próximo ano"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  </div>
);

const PainelMonthPicker = ({
  value,
  onChange,
  name,
  placeholder = "Selecione o mês",
  disabled = false,
  className = "",
  popperPlacement = "bottom-start",
}) => {
  const selected = isoMonthToDate(value);
  const displayValue = formatMonthDisplay(value);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const selectedYear = selected?.getFullYear();
    const start = (selectedYear ?? currentYear) - 10;
    const end = (selectedYear ?? currentYear) + 10;
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [selected]);

  const emitChange = (date) => {
    const nextValue = dateToIsoMonth(date);
    if (!nextValue || nextValue === value) return;

    if (typeof onChange === "function") {
      onChange({ target: { name, value: nextValue } });
    }
  };

  const customInput = useMemo(
    () => (
      <MonthInput
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
      />
    ),
    [displayValue, placeholder, disabled]
  );

  return (
    <div className={`fz-date-picker fz-month-picker ${className}`.trim()}>
      <DatePicker
        selected={selected}
        onChange={emitChange}
        locale="pt-BR"
        dateFormat="MMMM yyyy"
        showMonthYearPicker
        showFullMonthYearPicker
        placeholderText={placeholder}
        disabled={disabled}
        preventOpenOnFocus
        shouldCloseOnSelect
        popperPlacement={popperPlacement}
        popperClassName="fz-date-popper"
        calendarClassName="fz-date-calendar fz-month-calendar"
        minDate={new Date(`${yearOptions[0]}-01-01T00:00:00`)}
        maxDate={new Date(`${yearOptions[yearOptions.length - 1]}-12-31T23:59:59`)}
        renderCustomHeader={(headerProps) => (
          <MonthPickerHeader {...headerProps} />
        )}
        customInput={customInput}
      />
    </div>
  );
};

export default PainelMonthPicker;
